// src/pages/ManageSubscription.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { getAuth } from "firebase/auth";
import { Navigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

export default function ManageSubscription() {
  const [loading, setLoading] = useState(false);     // Stripe portal loading
  const [isPremium, setIsPremium] = useState(null);  // null = checking, true/false after
  const auth = getAuth();

  // Check premium status from Firestore
  useEffect(() => {
    const checkPremium = async () => {
      const user = auth.currentUser;
      if (!user) {
        setIsPremium(false);
        return;
      }

      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setIsPremium(!!data.premium);
        } else {
          setIsPremium(false);
        }
      } catch (err) {
        console.error("Error checking premium status:", err);
        setIsPremium(false);
      }
    };

    checkPremium();
  }, [auth]);

  const openPortal = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("You must be logged in to manage your subscription.");
        return;
      }

      const idToken = await user.getIdToken();

      const response = await fetch(
        "https://us-central1-reflectrise-c0ad4.cloudfunctions.net/api/create-portal-session",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text);
      }

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        alert(
          data.error ||
            "Unable to open subscription portal. Please try again."
        );
      }
    } catch (error) {
      console.error("❌ Error opening portal:", error);
      alert("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // If user is not premium, bounce them to the Premium page
  if (isPremium === false) {
    return <Navigate to="/premium" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Manage Subscription – ReflectRise</title>
        <meta
          name="description"
          content="Manage your ReflectRise Premium subscription — update your payment method, change plans, or cancel anytime securely via Stripe."
        />
        <meta
          property="og:title"
          content="Manage Subscription – ReflectRise"
        />
        <meta
          property="og:description"
          content="Easily access your ReflectRise billing portal to manage or cancel your subscription."
        />
        <meta
          property="og:image"
          content="https://reflectrise.co.uk/rsz_logo192.png"
        />
        <link
          rel="canonical"
          href="https://reflectrise.co.uk/manage-subscription"
        />
      </Helmet>

      {/* While we are checking premium state */}
      {isPremium === null ? (
        <div className="content manage-container">
          <div className="manage-card">
            <h1>Checking your subscription...</h1>
            <p>Please wait a moment.</p>
          </div>
        </div>
      ) : (
        // Premium user view
        <div className="content manage-container">
          <div className="manage-card">
            <h1>💎 Manage Your ReflectRise Subscription</h1>
            <p>
              Update your payment method, change your plan, or cancel anytime
              securely through the Stripe billing portal.
            </p>

            <button
              onClick={openPortal}
              disabled={loading}
              className="manage-btn"
            >
              {loading ? "Opening Portal..." : "Open Subscription Portal"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
